/**
 * GLOF Report Handler
 * Handles: GLOF Reporting Form (PDF)
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import govtLogo from '../Govt_logo.png';

const loadLogo = (src) => new Promise((resolve) => {
    const img = new Image();
    img.src = src;
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
});

const getSafeValue = (val) => (val !== null && val !== undefined && val !== '') ? String(val) : '0';

export const handleGlofReportPDF = async (message, format, rawData) => {
    if (format !== 'pdf') {
        alert('GLOF Report only supports PDF');
        return;
    }
    
    console.log('🌊 GLOF PDF generation started');
    const doc = new jsPDF('l', 'pt', 'a4');
    const centerX = doc.internal.pageSize.getWidth() / 2;
    const pageWidth = doc.internal.pageSize.getWidth();

    const logo = await loadLogo(govtLogo);
    if (logo) doc.addImage(logo, 'JPEG', centerX - 30, 20, 60, 60);

    const baseData = rawData?.data || rawData;
    const reportDate = message.debug?.tool_result?.parameters?.reported_on || baseData?.from_date || new Date().toISOString().split('T')[0];
    
    let displayDate = reportDate;
    if (displayDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [y, m, d] = displayDate.split('-');
        displayDate = `${d}/${m}/${y}`;
    }

    doc.setFontSize(13);
    doc.text('Govt. of ABC State', centerX, 95, { align: 'center' });
    doc.text('Department Name', centerX, 115, { align: 'center' });
    doc.text(`Glof Report (Dated on ${displayDate})`, centerX, 135, { align: 'center' });

    let dailyRecords = Array.isArray(baseData?.glof_report) ? baseData.glof_report : Array.isArray(baseData?.heatwave) ? baseData.heatwave : [];
    let cumRecords = Array.isArray(baseData?.glof_report_cum) ? baseData.glof_report_cum : Array.isArray(baseData?.glof_report_cumm) ? baseData.glof_report_cumm : Array.isArray(baseData?.heatwave_cumm) ? baseData.heatwave_cumm : [];
    
    const cumMap = {};
    cumRecords.forEach(r => { if (r.region_id || r.block_name) cumMap[r.region_id || r.block_name] = r; });

    const districtName = String(dailyRecords[0]?.dist_name || message.debug?.parameters?.district || '[DISTRICT]').toUpperCase();

    // ✅ FIX 2: Exact Header Text from the Official Web UI PDF
    const headers = [
      'S.No.', 'Block Name', 
      'Number of\nDeaths due\nto Glof\n(Today)', 
      'Number of\nDeaths due\nto Glof\n(Cumulative)',
      'Number of\ndeceased\nperson for\nwhom\nex-gratia\npayment\ndone (Today)', 
      'Number of\ndeceased\nperson for\nwhom\nex-gratia\npayment\ndone\n(Cumulative)',
      'Number of\npersons\nhospitalized\n(Today)', 
      'Number of\npersons\nhospitalized\n(Cumulative)',
      'Number of\nhospitalized\nperson for\nwhom\npayment\ndone (Today)', 
      'Number of\nhospitalized\nperson for\nwhom\npayment\ndone\n(Cumulative)',
      'Remarks'
    ];

    // ✅ FIX 3: Re-ordered Columns (Ex-Gratia now comes before Hospitalized)
    const body = dailyRecords.map((row, i) => {
      const cum = cumMap[row.region_id || row.block_name] || {};
      return [
        String(i + 1),
        row.block_name || 'NA',
        getSafeValue(row.no_of_deaths_today), getSafeValue(cum.no_of_deaths_cum),
        getSafeValue(row.no_of_dec_ex_gratia_pay_done_today), getSafeValue(cum.no_of_dec_ex_gratia_pay_done_cum), 
        getSafeValue(row.no_of_persons_hospitalized_today), getSafeValue(cum.no_of_persons_hospitalized_cum),
        getSafeValue(row.no_of_persons_hospitalized_pay_done_today), getSafeValue(cum.no_of_persons_hospitalized_pay_done_cum),
        row.remarks || 'NA'
      ];
    });

    if (body.length === 0) return alert('No data available');

    autoTable(doc, {
        head: [headers],
        body: body,
        startY: 160,
        theme: 'grid',
        // ✅ FIX 1: Moving line color/width to global styles ensures ALL borders are uniformly black and crisp!
        styles: { 
            lineColor: [0, 0, 0], 
            lineWidth: 0.1,       
            cellPadding: 4, 
            overflow: 'linebreak',
            font: 'Helvetica', 
            fontSize: 8 
        },
        headStyles: { 
            fillColor: [255, 255, 255], 
            textColor: [0, 0, 0], 
            fontStyle: 'bold', 
            halign: 'center' 
        },
        bodyStyles: { 
            halign: 'center' 
        }
    });

    const finalY = doc.lastAutoTable.finalY || 200;
    const footerY = Math.max(finalY + 60, doc.internal.pageSize.getHeight() - 120);

    doc.setFontSize(10);
    doc.text('Letter No.:', 50, footerY);
    doc.text('Date:', 50, footerY + 20);
    doc.text('Copy:', 50, footerY + 40);
    doc.text('Signature of District Officers/', pageWidth - 250, footerY);
    doc.text('Additional District Magistrate (ADM) (Disaster Management)/', pageWidth - 250, footerY + 15);
    doc.text('Officer in Charge (District Disaster Management)/', pageWidth - 250, footerY + 30);
    doc.setFont('Helvetica', 'bold');
    doc.text(`[${districtName} DISTRICT]`, pageWidth - 250, footerY + 50);

    const now = new Date(new Date().getTime() + (5.5 * 60 * 60 * 1000));
    const ts = `${now.toISOString().split('T')[0]}_${now.toISOString().split('T')[1].split('.')[0].replace(/:/g, '')}`;
    doc.save(`daily_glof_report_${ts}.pdf`);
};

export const isGlofReport = (toolName) => ['glof_reporting_form', 'glofreportingform'].includes(toolName?.toLowerCase());
export const isGlofStatus = (toolName) => ['glof_status', 'glof_reporting_status', 'glof_daily_report_status'].some(n => toolName?.toLowerCase().includes(n));
export const isGlofMIS = (toolName) => toolName?.toLowerCase().includes('glof_mis');